/**
 * Dashboard Filters Component
 *
 * Slide-in panel for filtering dashboard analytics
 */

import { Button } from '@/shared/ui/button';
import { Filter, X } from 'lucide-react';
import { useJobCategories, usePriorities, useUsers } from '../hooks/useDashboardFilters';
import type { DashboardFilters as FiltersType } from '../types/analytics';
import JobCategorySelect from './JobCategorySelect';
import UserSelect from './UserSelect';

interface DashboardFiltersProps {
  isOpen: boolean;
  onClose: () => void;
  filters: FiltersType;
  onFiltersChange: (filters: FiltersType) => void;
  onApply: () => void;
  onClear: () => void;
}

const dateRangeOptions = [
  { value: 'last30', label: 'Last 30 days' },
  { value: 'last90', label: 'Last 90 days' },
  { value: 'last6m', label: 'Last 6 months' },
  { value: 'ytd', label: 'Year to date' },
  { value: 'all', label: 'All time' },
] as const;

export default function DashboardFilters({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
  onApply,
  onClear,
}: DashboardFiltersProps) {
  const { data: jobCategoriesData, isLoading: isLoadingCategories } = useJobCategories();
  const { data: usersData, isLoading: isLoadingUsers } = useUsers();
  const { data: prioritiesData, isLoading: isLoadingPriorities } = usePriorities();

  if (!isOpen) return null;

  const jobCategories = jobCategoriesData?.values || [];
  const users = usersData?.values || [];
  const priorities = prioritiesData?.values || [];

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40"
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        role="button"
        tabIndex={0}
        aria-label="Close filter panel"
      />

      {/* Slide-in panel */}
      <div className="fixed top-0 right-0 h-full w-80 bg-card border-l border-border shadow-2xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <span className="font-semibold text-foreground">Dashboard Filters</span>
          </div>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Filter content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
          {/* Date Range */}
          <div>
            <div className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              Date Range
            </div>
            <div className="space-y-1">
              {dateRangeOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => onFiltersChange({ ...filters, dateRange: option.value })}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                    filters.dateRange === option.value
                      ? 'bg-orange-600 text-white'
                      : 'hover:bg-accent text-foreground'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Job Type */}
          <div>
            <div className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              Job Type
            </div>
            {isLoadingCategories ? (
              <div className="text-sm text-muted-foreground">Loading job types...</div>
            ) : (
              <JobCategorySelect
                categories={jobCategories}
                value={filters.jobType}
                onChange={(value) => onFiltersChange({ ...filters, jobType: value })}
                isLoading={isLoadingCategories}
              />
            )}
          </div>

          {/* Priority */}
          <div>
            <div className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              Priority
            </div>
            {isLoadingPriorities ? (
              <div className="text-sm text-muted-foreground">Loading priorities...</div>
            ) : (
              <div className="flex gap-2 flex-wrap">
                <button
                  key="all"
                  onClick={() => onFiltersChange({ ...filters, priority: 'all' })}
                  className={`px-3 py-1.5 rounded-lg text-xs transition-colors border ${
                    filters.priority === 'all'
                      ? 'bg-orange-600 text-white border-orange-600'
                      : 'border-border hover:bg-accent text-foreground'
                  }`}
                >
                  All
                </button>
                {priorities.map((priority) => (
                  <button
                    key={priority.id}
                    onClick={() => onFiltersChange({ ...filters, priority: String(priority.id) })}
                    className={`px-3 py-1.5 rounded-lg text-xs transition-colors border ${
                      filters.priority === String(priority.id)
                        ? 'bg-orange-600 text-white border-orange-600'
                        : 'border-border hover:bg-accent text-foreground'
                    }`}
                  >
                    {priority.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Assignee */}
          <div>
            <div className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              Assignee
            </div>
            {isLoadingUsers ? (
              <div className="text-sm text-muted-foreground">Loading users...</div>
            ) : (
              <UserSelect
                users={users}
                value={filters.assignee}
                onChange={(value) => onFiltersChange({ ...filters, assignee: value })}
                isLoading={isLoadingUsers}
              />
            )}
          </div>
        </div>

        {/* Footer actions */}
        <div className="px-5 py-4 border-t border-border flex gap-3">
          <Button variant="outline" className="flex-1 border-border" onClick={onClear}>
            Clear All
          </Button>
          <Button className="flex-1 bg-orange-600 hover:bg-orange-700 text-white" onClick={onApply}>
            Apply Filters
          </Button>
        </div>
      </div>
    </>
  );
}
